import { useState } from "react";
import { Send, MapPin, Phone, Mail, Clock, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Hero Section */}
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-blue-100/70 leading-relaxed">
              Have questions about our services? Get in touch and we'll be happy to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-[#002055] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Form */}
            <div className="form-dark">
              <h2 className="text-2xl font-bold text-white mb-6">Send us a Message</h2>
              
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">Message Sent!</h3>
                  <p className="text-blue-100/60 mb-6">We'll get back to you soon.</p>
                  <Button onClick={() => setSubmitted(false)} className="btn-secondary">
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-blue-100">Name *</Label>
                      <Input name="name" value={formData.name} onChange={handleChange} required className="input-dark" placeholder="Your name" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-blue-100">Email *</Label>
                      <Input name="email" type="email" value={formData.email} onChange={handleChange} required className="input-dark" placeholder="your@email.com" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-blue-100">Phone</Label>
                      <Input name="phone" type="tel" value={formData.phone} onChange={handleChange} className="input-dark" placeholder="Phone number" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-blue-100">Subject *</Label>
                      <Input name="subject" value={formData.subject} onChange={handleChange} required className="input-dark" placeholder="How can we help?" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-blue-100">Message *</Label>
                    <Textarea name="message" value={formData.message} onChange={handleChange} required className="input-dark min-h-[140px]" placeholder="Your message..." />
                  </div>

                  <Button type="submit" className="w-full btn-primary" disabled={loading}>
                    {loading ? "Sending..." : <>Send Message <Send className="w-4 h-4 ml-2" /></>}
                  </Button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Get in Touch</h2>
                <p className="text-blue-100/60 leading-relaxed">
                  We're here to answer any questions about our currency exchange services.
                </p>
              </div>

              <div className="space-y-4">
                <div className="contact-card">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Visit Us</h4>
                    <p className="text-blue-100/60">2-723 12th Street, New Westminster, BC</p>
                    <a href="https://maps.google.com/?q=2-723+12th+Street+New+Westminster+BC" target="_blank" rel="noopener noreferrer" className="text-blue-400 text-sm hover:underline mt-1 inline-block">
                      Get Directions →
                    </a>
                  </div>
                </div>

                <div className="contact-card">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Call Us</h4>
                    <a href="tel:+17787590400" className="text-blue-100/60 hover:text-white text-lg">+1 (778) 759-0400</a>
                  </div>
                </div>

                <div className="contact-card">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Email Us</h4>
                    <a href="mailto:info@fonderx.com" className="text-blue-100/60 hover:text-white">info@fonderx.com</a>
                  </div>
                </div>

                <div className="contact-card">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Business Hours</h4>
                    <p className="text-blue-100/60">Mon-Fri: 9:30AM - 6PM</p>
                    <p className="text-blue-100/60">Sat - Sun: Closed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-[#001a40] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Find Us</h2>
            <p className="text-blue-100/60">Conveniently located in New Westminster, BC</p>
          </div>
          <div className="rounded-2xl overflow-hidden h-[400px] shadow-lg border border-white/10">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2607.5!2d-122.912!3d49.206!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5485d8b0a1b8c5e5%3A0x0!2s723%2012th%20St%2C%20New%20Westminster%2C%20BC!5e0!3m2!1sen!2sca!4v1"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title="FonderX Location"
              className="grayscale-[20%]"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
}
